import * as React from 'react'
import { useState, useEffect } from "react";

import {
    Column,
    ColumnDef,
    flexRender,
    getCoreRowModel,
    createColumnHelper,
    getFilteredRowModel,
    getPaginationRowModel,
    Table,
    useReactTable,
} from '@tanstack/react-table'

export type ApiProduct = {
    productNumber: string
    brand: string
    category: string
    option1: string
    totalPlannedQuantity: number
    supplier: string
    collection: number
    onHand: number
    onOrder: number,
    periods: Array<Period>
}

export type Product = {
    productNumber: string
    brand: string
    category: string
    option1: string
    totalPlannedQuantity: number
    supplier: string
    collection: number
    onHand: number
    onOrder: number,
    period1: number,
    period2: number,
    period3: number,
    period4: number,
    period5: number,
    period6: number
}

export type Period = {
    period: number
    plannedQuantity: number,
    plannedOrders: Array<PlannedOrder>
}

export type PlannedOrder = {
    orderId: string
    quantity: number
    lineItems: Array<PlannedOrderLineItem>
}

export type PlannedOrderLineItem = {
    locationNumber: number
    quantity: number
    option2: string 
}

const defaultData: Product[] = [
    {
        productNumber: '123457890',
        brand: 'Adidas',
        category: 'HerrByxa',
        supplier: 'A.Supplier',
        totalPlannedQuantity: 50,
        collection: 20241,
        onHand: 200,
        onOrder: 150,
        option1: 'Red',
        period1: 150,
        period2: 150,
        period3: 150,
        period4: 150,
        period5: 150,
        period6: 150
    }
]

const columnHelper = createColumnHelper<Product>()

const columns = [
    columnHelper.accessor('collection', {
        cell: info => info.getValue(),
        header: () => <span>Collection</span>,
        footer: info => info.column.id,
    }),
    columnHelper.accessor(row => row.category, {
        id: 'category',
        cell: info => <i>{info.getValue()}</i>,
        header: () => <span>Category</span>,
        footer: info => info.column.id,
    }),
    columnHelper.accessor(row => row.brand, {
        id: 'brand',
        cell: info => <i>{info.getValue()}</i>,
        header: () => <span>Brand</span>,
        footer: info => info.column.id,
    }),
    columnHelper.accessor('supplier', {
        id: 'supplier',
        cell: info => info.getValue(),
        header: () => <span>Supplier</span>,
        footer: info => info.column.id,
    }),
    columnHelper.accessor('productNumber', {
        id: 'productNumber',
        cell: info => info.getValue(),
        header: () => <span>Product Number</span>,
        footer: info => info.column.id,
    }),
    columnHelper.accessor('option1', {
        id: 'option1',
        cell: info => info.getValue(),
        header: () => <span>Option1</span>,
        footer: info => info.column.id,
    }),
    columnHelper.accessor('onHand', {
        id: 'onHand',
        cell: info => info.getValue(),
        header: () => <span>On Hand</span>,
        footer: info => info.column.id,
    }),
    columnHelper.accessor('onOrder', {
        id: 'onOrder',
        cell: info => info.getValue(),
        header: () => <span>On Order</span>,
        footer: info => info.column.id,
    }),
    columnHelper.accessor('totalPlannedQuantity', {
        id: 'totalPlannedQuantity',
        cell: info => info.getValue(),
        header: () => <span>Total Planned Quantity</span>,
        footer: info => info.column.id,
    }),
    columnHelper.accessor('period1', {
        id: 'period1',
        cell: info => info.getValue(),
        header: () => <span>Period 1</span>,
        footer: info => info.column.id,
    }),
    columnHelper.accessor('period2', {
        id: 'period2',
        cell: info => info.getValue(),
        header: () => <span>Period 2</span>,
        footer: info => info.column.id,
    }),
    columnHelper.accessor('period3', {
        id: 'period3',
        cell: info => info.getValue(),
        header: () => <span>Period 3</span>,
        footer: info => info.column.id,
    }),
    columnHelper.accessor('period4', {
        id: 'period4',
        cell: info => info.getValue(),
        header: () => <span>Period 4</span>,
        footer: info => info.column.id,
    }),
    columnHelper.accessor('period5', {
        id: 'period5',
        cell: info => info.getValue(),
        header: () => <span>Period 5</span>,
        footer: info => info.column.id,
    }),
    columnHelper.accessor('period6', {
        id: 'period6',
        cell: info => info.getValue(),
        header: () => <span>Period 6</span>,
        footer: info => info.column.id,
    })
]

const AssortmentPlanningProductTable = (props: { products: Array<Product>, onCurrentProductChanged: any, onCurrentCellChanged: any }) => {
    const [data, setData] = useState(() => [...defaultData])
    //  const [rowSelection, setRowSelection] = useState({})
    // const [rowInfo, setRowInfo] = useState<Product>()
    const table = useReactTable({
        data,
        columns,
        enableRowSelection: true, //enable row selection for all rows
        // enableRowSelection: row => row.original.age > 18, // or enable row selection conditionally per row
        // onRowSelectionChange: setRowSelection,
        getCoreRowModel: getCoreRowModel(),
        debugTable: true,
    })

    const onRowClicked = (event: any) => {
        //  setRowInfo(event);
        props.onCurrentProductChanged(event);
    }

    const onCellClicked = (event: any) => {
        var period = -1;

        switch (event) {
            case "period1":
                period = 1;
                break;
            case "period2":
                period = 2;
                break;
            case "period3":
                period = 3;
                break;
            case "period4":
                period = 4;
                break;
            case "period5":
                period = 5;
                break;
            case "period6":
                period = 6;
                break;
        }
        props.onCurrentCellChanged(period);
    }

    useEffect(() => {
        setData(props.products);
    }, [props.products])

    return (
        <table border={1}>
            <thead>
                {table.getHeaderGroups().map(headerGroup => (
                    <tr key={headerGroup.id}>
                        {headerGroup.headers.map(header => (
                            <th key={header.id}>
                                {header.isPlaceholder
                                    ? null
                                    : flexRender(
                                        header.column.columnDef.header,
                                        header.getContext()
                                    )}
                            </th>
                        ))}
                    </tr>
                ))}
            </thead>
            <tbody>
                {table.getRowModel().rows.map(row => {
                    return (
                        <tr key={row.id} onClick={() => onRowClicked(row.original)}>
                            {row.getVisibleCells().map(cell => {
                                return (
                                    <td style={{ color: cell.column.id === 'onHand' && row.original.onHand < 500 ? "red" : "black" }} key={cell.id} onClick={() => onCellClicked(cell.column.id)}>
                                        {flexRender(
                                            cell.column.columnDef.cell,
                                            cell.getContext()
                                        )}
                                    </td>
                                )
                            })}
                        </tr>
                    )
                })}
            </tbody>
        </table>
    );
}

export default AssortmentPlanningProductTable;